import { type UseMutationOptions, useMutation, useQuery } from "@tanstack/react-query"
import customInstance from "@/api/instance"
import type {
  ChangeEmailResponse,
  ChangePasswordResponse,
  ForgetPasswordResponse,
  ResetPasswordResponse,
  SessionResponse,
  SignInEmailRequest,
  SignInEmailResponse,
  SignUpEmailResponse,
  UpdateProfileResponse,
} from "./models"
import {
  type ChangeEmailRequest,
  type ChangePasswordRequest,
  changeEmailRequestSchema,
  changePasswordRequestSchema,
  type ForgetPasswordRequest,
  forgetPasswordRequestSchema,
  type ResetPasswordRequest,
  resetPasswordRequestSchema,
  type SignUpEmailRequest,
  signInEmailRequestSchema,
  signUpEmailRequestSchema,
  type UpdateProfileRequest,
  updateProfileRequestSchema,
} from "./schemas"

/**
 * サインアップを行うカスタムフック
 * @param options
 * @returns
 */
export function useSignUpEmail(
  options?: UseMutationOptions<SignUpEmailResponse, Error, SignUpEmailRequest>
) {
  return useMutation({
    mutationFn: (data: SignUpEmailRequest) => {
      const validatedData = signUpEmailRequestSchema.parse(data)

      return customInstance<SignUpEmailResponse>("/api/auth/sign-up/email", {
        method: "POST",
        body: JSON.stringify(validatedData),
      })
    },
    ...options,
  })
}

/**
 * サインインを行うカスタムフック
 * @param options
 * @returns
 */
export function useSignInEmail(
  options?: UseMutationOptions<SignInEmailResponse, Error, SignInEmailRequest>
) {
  return useMutation({
    mutationFn: (data: SignInEmailRequest) => {
      const validatedData = signInEmailRequestSchema.parse(data)

      return customInstance<SignInEmailResponse>("/api/auth/sign-in/email", {
        method: "POST",
        body: JSON.stringify(validatedData),
      })
    },
    ...options,
  })
}

/**
 * サインアウトを行うカスタムフック
 * @param options
 * @returns
 */
export function useSignOut(options?: UseMutationOptions<{ success: boolean }, Error>) {
  return useMutation({
    mutationFn: () => {
      return customInstance<{ success: boolean }>("/api/auth/sign-out", {
        method: "POST",
        body: JSON.stringify({}),
      })
    },
    ...options,
  })
}

// useQueryを使ってuseGetSessionを作成
export function useGetSession() {
  return useQuery<SessionResponse>({
    queryKey: ["get-session"],
    queryFn: async () =>
      customInstance<SessionResponse>("/api/auth/get-session", {
        method: "GET",
      }),
  })
}

type SignInOAuthRequest = {
  callbackURL?: string
}

type SignInOAuthResponse = {
  url: string
  redirect: boolean
}

type SignInLineRequest = SignInOAuthRequest
type SignInLineResponse = SignInOAuthResponse
type SignInEchoesRequest = SignInOAuthRequest
type SignInEchoesResponse = SignInOAuthResponse

/**
 * LINEログインを開始するカスタムフック
 * better-authのgenericOAuthプラグインを使用
 */
export function useSignInLine(
  options?: UseMutationOptions<SignInLineResponse, Error, SignInLineRequest>
) {
  return useMutation({
    mutationFn: async (data: SignInLineRequest) => {
      // callbackURLを動的に設定（デフォルトはホームページ）
      const callbackURL = data.callbackURL
        ? `${window.location.origin}${data.callbackURL}`
        : window.location.origin

      const response = await customInstance<SignInLineResponse>("/api/auth/sign-in/oauth2", {
        method: "POST",
        body: JSON.stringify({
          providerId: "line",
          callbackURL,
        }),
      })
      return response
    },
    ...options,
  })
}

/**
 * Echoes SSOログインを開始するカスタムフック
 * better-authのgenericOAuthプラグインを使用（OIDC Discovery経由）
 */
export function useSignInEchoes(
  options?: UseMutationOptions<SignInEchoesResponse, Error, SignInEchoesRequest>
) {
  return useMutation({
    mutationFn: async (data: SignInEchoesRequest) => {
      const callbackURL = data.callbackURL
        ? `${window.location.origin}${data.callbackURL}`
        : window.location.origin

      const response = await customInstance<SignInEchoesResponse>("/api/auth/sign-in/oauth2", {
        method: "POST",
        body: JSON.stringify({
          providerId: "echoes",
          callbackURL,
        }),
      })
      return response
    },
    ...options,
  })
}

/**
 * プロフィールを更新するカスタムフック
 * better-authの/api/auth/update-userエンドポイントを使用
 * @param options
 * @returns
 */
export function useUpdateProfile(
  options?: UseMutationOptions<UpdateProfileResponse, Error, UpdateProfileRequest>
) {
  return useMutation({
    mutationFn: (data: UpdateProfileRequest) => {
      const validatedData = updateProfileRequestSchema.parse(data)

      // フロントエンドのフィールドをbetter-authのスキーマにマッピング
      const nameParts = validatedData.name.split(/\s+/)
      const lastName = nameParts[0] || ""
      const firstName = nameParts.slice(1).join(" ") || ""

      const nameKanaParts = (validatedData.nameKana ?? "").split(/\s+/)
      const lastNameKana = nameKanaParts[0] || ""
      const firstNameKana = nameKanaParts.slice(1).join(" ") || ""

      const payload = {
        name: validatedData.name,
        firstName,
        lastName,
        firstNameKana,
        lastNameKana,
        phoneNumber: validatedData.phone,
        emergencyPhoneNumber: validatedData.emergencyPhone || "",
        postalCode: validatedData.postalCode,
        address: validatedData.address,
      }

      return customInstance<UpdateProfileResponse>("/api/auth/update-user", {
        method: "POST",
        body: JSON.stringify(payload),
      })
    },
    ...options,
  })
}

/**
 * メールアドレスを変更するカスタムフック
 * @param options
 * @returns
 */
export function useChangeEmail(
  options?: UseMutationOptions<ChangeEmailResponse, Error, ChangeEmailRequest>
) {
  return useMutation({
    mutationFn: (data: ChangeEmailRequest) => {
      const validatedData = changeEmailRequestSchema.parse(data)

      return customInstance<ChangeEmailResponse>("/api/auth/change-email", {
        method: "POST",
        body: JSON.stringify({
          ...validatedData,
          callbackURL: `${window.location.origin}/`,
        }),
      })
    },
    ...options,
  })
}

/**
 * パスワードを変更するカスタムフック
 * @param options
 * @returns
 */
export function useChangePassword(
  options?: UseMutationOptions<ChangePasswordResponse, Error, ChangePasswordRequest>
) {
  return useMutation({
    mutationFn: (data: ChangePasswordRequest) => {
      const validatedData = changePasswordRequestSchema.parse(data)

      return customInstance<ChangePasswordResponse>("/api/auth/change-password", {
        method: "POST",
        body: JSON.stringify(validatedData),
      })
    },
    ...options,
  })
}

/**
 * パスワードリセットメールを送信するカスタムフック
 * @param options
 * @returns
 */
export function useForgetPassword(
  options?: UseMutationOptions<ForgetPasswordResponse, Error, ForgetPasswordRequest>
) {
  return useMutation({
    mutationFn: (data: ForgetPasswordRequest) => {
      const validatedData = forgetPasswordRequestSchema.parse(data)
      const redirectTo = `${window.location.origin}/password-reset`

      return customInstance<ForgetPasswordResponse>("/api/auth/request-password-reset", {
        method: "POST",
        body: JSON.stringify({ ...validatedData, redirectTo }),
      })
    },
    ...options,
  })
}

/**
 * パスワードをリセットするカスタムフック
 * @param options
 * @returns
 */
export function useResetPassword(
  options?: UseMutationOptions<ResetPasswordResponse, Error, ResetPasswordRequest>
) {
  return useMutation({
    mutationFn: (data: ResetPasswordRequest) => {
      const validatedData = resetPasswordRequestSchema.parse(data)

      return customInstance<ResetPasswordResponse>("/api/auth/reset-password", {
        method: "POST",
        body: JSON.stringify(validatedData),
      })
    },
    ...options,
  })
}
